"use client";

import "./globals.css";
import { ReactNode, useEffect, useState } from "react";
import ClientProvider from "../components/ClientProvider";
import { SessionProvider, signOut, useSession } from "next-auth/react";
import { Theme, seismicTheme, Button, Toasts } from "@seismic/mantle";
import Navbar from "../components/Navbar";
import { usePathname } from 'next/navigation'

const AppToasts = Toasts as unknown as React.ComponentType<{
  children?: ReactNode;
  style?: React.CSSProperties;
}>;

function SessionLayout({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const { data: session, status } = useSession();
  const isGrayBackground = pathname === '/' || pathname === '/home' || pathname === '/available-partners';
  const [isSessionExpired, setIsSessionExpired] = useState(false);
  const [hasEverAuthenticated, setHasAuthenticated] = useState(false);
  const [enableRenewSession, setEnableRenewSession] = useState(false);
  const isNoPermissionPage = pathname === '/login' || pathname === '/401Error';

  useEffect(() => {
    if (!session?.expires || new Date(session.expires) < new Date()) {
      setIsSessionExpired(true);
    } else {
      setIsSessionExpired(false);
    }

    if(status === "authenticated") {
      setHasAuthenticated(true);
    }

  }, [session, status]);

  useEffect(() => {
    if(!isNoPermissionPage && hasEverAuthenticated && isSessionExpired) {
      setEnableRenewSession(true);
    }
  }, [isSessionExpired, hasEverAuthenticated, isNoPermissionPage]);

  return (
    <body
      style={{
        fontFamily: 'Source Sans Pro',
        height: '100%',
        backgroundColor: isGrayBackground ? '#F3F4F6' : undefined,
      }}
    >
      <Theme
        theme={{
          ...seismicTheme({ useGlobal: true }),
        }}
      >
        <AppToasts style={{ position: 'fixed', bottom: 20, right: 20 }}>
          <ClientProvider>
            <div style={{ filter: enableRenewSession ? 'blur(20px)' : 'none', width: '100%' }}>
              {pathname !== '/login' && (
                <div style={{ position: 'fixed', width: '100%', zIndex: 1000 }}>
                  <Navbar />
                </div>
              )}
              <div style={{ display: 'flex', height: '100%' }}>
                {children}
              </div>
            </div>
            {enableRenewSession && (
              <div style={{
                position: 'fixed',
                zIndex: 9999,
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                background: 'rgba(84, 91, 100, 0.75)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <div style={{
                  background: '#fff',
                  borderRadius: 8,
                  padding: 18,
                  minWidth: 320,
                  maxWidth: 500,
                  boxShadow: 'rgba(83, 91, 100, 0.32) 0px 16px 24px',
                  textAlign: 'center'
                }}>
                  <div className="mb-4 text-left font-size-18">
                  <h3>Session Expired</h3>
                  </div>
                  <hr style={{ margin: '18px -18px' }} />
                  <div className='mt-10' style={{ color: '#6e6f6f' }}>
                  <p>Your session has expired.</p>
                  <p>Click below to renew your session. This will open a new window and you will be prompted to log in again.</p>
                  </div>
                  <Button
                  variant="primary"
                  label="Renew Session"
                  onClick={() => signOut({ callbackUrl: pathname })}
                  style={{ marginTop: 16 }}
                  />
                </div>
              </div>
            )}
          </ClientProvider>
        </AppToasts>
      </Theme>
    </body>
  );
}

export default function RootLayout({
  children,
}: Readonly<{
  children: ReactNode;
}>) {
  return (
    <html lang="en">
      <head></head>
      <SessionProvider>
        <SessionLayout>{children}</SessionLayout>
      </SessionProvider>
    </html>
  );
}